import { apiPost, type Envelope } from "./client.js";
import { useApi, type ApiState } from "./useApi.js";
import type { Role } from "../roles.js";

/**
 * E8.10 (NX-2638) — the capability-assessment service: the only place the Assessment screen learns
 * how to reach the API. The screen and its sub-components take an `AssessmentService` and never
 * call `apiPost`/`fetch` themselves (AC6).
 *
 * Shapes mirror apps/api/src/capability-assessment.ts rather than being redeclared loosely. As with
 * `roles.ts` and `projectMetrics.ts`, they are MIRRORED rather than imported: `@senesight/core` and
 * `@senesight/connectors` carry Node-only code, so the browser bundle never imports them.
 * `assessmentService.test.ts` binds both the question set and the role lists to their canonical
 * definitions in a Node-environment test, so a dimension added upstream fails the build here rather
 * than quietly going unasked.
 */

export interface CapabilityAnswerInput {
  readonly dimension: string;
  readonly value: string | number | boolean;
}

export interface CapabilitySignalView {
  readonly id: number;
  readonly dimension: string;
  readonly value: string | number | boolean;
  /** Masked by the API for roles outside the responder set — absent, never a guessed name (AC5). */
  readonly responder?: string;
  readonly confidence: number;
  readonly method: string;
  readonly effectiveAt: string;
  readonly recordedAt: string;
}

export interface CapabilityAssessmentView {
  readonly projectId: string;
  readonly version: number;
  readonly responder?: string;
  readonly submittedAt: string;
  readonly recordedAt: string;
  readonly signals: readonly CapabilitySignalView[];
}

/** One question. Mirrors CapabilityDimension in packages/connectors/src/questionnaire.ts. */
/** The shape GET /projects/:id/capability-assessments actually returns. */
export interface CapabilityAssessmentHistory {
  readonly projectId: string;
  readonly versions: readonly CapabilityAssessmentView[];
}

/**
 * Pull the versions out of whatever the history endpoint returned, tolerating a shape that is not
 * what we expect. A screen must degrade to "no history" rather than throw — an exception here
 * unmounts the entire app, navigation included, which is what made a small typing mistake look
 * like a total outage.
 */
export const versionsOf = (
  history: CapabilityAssessmentHistory | null | undefined,
): readonly CapabilityAssessmentView[] =>
  Array.isArray(history?.versions) ? history.versions : [];

export interface CapabilityQuestion {
  readonly key: string;
  readonly prompt: string;
}

/**
 * The question set the form renders. The form is driven BY this list — there is no per-dimension
 * markup anywhere in the screen — so adding a dimension upstream is additive here too (AC1).
 */
export const CAPABILITY_QUESTIONS: readonly CapabilityQuestion[] = [
  { key: "trunk_based_development", prompt: "Does the team practice trunk-based development?" },
  { key: "ci_on_every_change", prompt: "Does every change run through CI before merge?" },
  { key: "code_review_required", prompt: "Is code review required before merge?" },
  { key: "automated_deploys", prompt: "Are deploys automated (no manual release steps)?" },
  { key: "estimates_tracked", prompt: "Does the team capture estimates for its work items?" },
];

/**
 * Mirrors CAPABILITY_ASSESSMENT_SUBMIT_ROLES / _VIEW_ROLES. The client gate exists so an
 * unauthorized role sees no submit affordance at all (AC2) — it does NOT replace the server's
 * `requireRole`, which remains the actual control. Two expressions of one boundary, bound together
 * by the drift test.
 */
export const ASSESSMENT_SUBMIT_ROLES: readonly Role[] = ["admin", "delivery-lead"];
export const ASSESSMENT_VIEW_ROLES: readonly Role[] = [
  "admin",
  "delivery-lead",
  "sponsor",
  "client-viewer",
  "readonly",
];

export const canSubmitAssessment = (role: Role): boolean => ASSESSMENT_SUBMIT_ROLES.includes(role);

export interface AssessmentService {
  /** The project's current assessment, or `null` when none has ever been submitted. */
  useCurrent(projectId: string): ApiState<CapabilityAssessmentView | null>;
  /**
   * The version history. The API returns `envelope({ projectId, versions })` — an OBJECT, not a
   * bare array (apps/api/src/capability-assessment.ts). Typing it as an array is what blanked the
   * screen on 2026-08-20: `.map` on the envelope object threw and unmounted the whole tree.
   */
  useHistory(projectId: string): ApiState<CapabilityAssessmentHistory>;
  useProjects(): ApiState<Array<{ id: string; name: string }>>;
  submit(
    projectId: string,
    answers: readonly CapabilityAnswerInput[],
    token: string | null,
  ): Promise<Envelope<CapabilityAssessmentView>>;
}

export const liveAssessmentService: AssessmentService = {
  useCurrent: (projectId) =>
    useApi<CapabilityAssessmentView | null>(
      `/projects/${projectId}/capability-assessments/current`,
    ),
  useHistory: (projectId) =>
    useApi<CapabilityAssessmentHistory>(`/projects/${projectId}/capability-assessments`),
  useProjects: () => useApi<Array<{ id: string; name: string }>>("/portfolio"),
  submit: (projectId, answers, token) =>
    apiPost<CapabilityAssessmentView>(`/projects/${projectId}/capability-assessments`, {
      token,
      body: { answers },
    }),
};
